import { Button, TextInput } from "@jugl-web/ui-kit";
import { isValidEmail, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useState } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export const EnterEmailStep: React.FC<{
  email: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  isLoading?: boolean;
}> = ({ email, onEmailChange, onContinue, isLoading = false }) => {
  const { t } = useTranslations();
  const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

  const isEmailValid = isValidEmail(email);
  const isEmailError = hasAttemptedContinue && !isEmailValid;

  const handleContinue = useCallback(() => {
    if (isEmailValid) {
      setHasAttemptedContinue(false);
      onContinue();
      return;
    }

    setHasAttemptedContinue(true);
  }, [isEmailValid, onContinue]);

  return (
    <div className="flex max-w-[600px] flex-col gap-10">
      <div className="flex gap-4">
        <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
        <div className="flex flex-col gap-1">
          <span className="font-secondary text-lg font-medium text-dark">
            {t({
              id: "client-support-page.enter-email",
              defaultMessage: "Enter email",
            })}
          </span>
          <span className="font-secondary text-[13px] leading-[150%] text-dark-700">
            {t({
              id: "client-support-page.enter-email-description",
              defaultMessage:
                "Indicate email address you would like to use in Jugl",
            })}
          </span>
        </div>
      </div>

      <TextInput
        labelPosition="border-overlap"
        label={t({
          id: "client-support-page.your-email",
          defaultMessage: "Your Email",
        })}
        placeholder="example@company.com"
        value={email}
        onChange={(event) =>
          onEmailChange(event.target.value.trim().toLowerCase())
        }
        isInvalid={isEmailError}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleContinue();
          }
        }}
        errorMessage={
          isEmailError
            ? t({
                id: "client-support-page.incorrect-email-format",
                defaultMessage: "Incorrect email format",
              })
            : undefined
        }
        autoFocus
      />

      <Button
        variant="primary"
        iconEnd={<ArrowRightIcon />}
        onClick={handleContinue}
        isDisabled={!email || isEmailError}
        isLoading={isLoading}
        className="w-[280px]"
      >
        {t({
          id: "common.continue",
          defaultMessage: "Continue",
        })}
      </Button>
    </div>
  );
};
